import { useHistory } from "react-router";
import { Box, Typography } from "@mui/material";

import {
  type AddPermissionsEventType,
  type CloudProviderTag,
  InsightProviderId,
  type InsightProviderWithPermissions,
} from "../../types";
import ProviderCard from "./ProviderCard";

type Providers = {
  [key in InsightProviderWithPermissions]: {
    cloudTag: CloudProviderTag;
    title: string;
    description: string;
    buttonText: string;
    url: string;
    eventType: AddPermissionsEventType;
  };
};

export type PermissionsNeededAccounts = {
  [key in
    | InsightProviderId.TRUSTED_ADVISOR
    | InsightProviderId.COST_OPTIMIZATION_HUB
    | InsightProviderId.SECURITY_HUB]: string[];
};

export type PermissionsNeededProvider = {
  id: InsightProviderId;
  insightsNum: number;
};

type PermissionsNeededProps = {
  permissionsNeededProviders: PermissionsNeededProvider[];
  customerId: string;
  permissionsNeededAccounts: PermissionsNeededAccounts;
  nonHealthyOrganizationCloudConnects: string[];
  hasMissingCloudConnectPermissions: boolean;
  onAddPermissionsClicked: (eventType: AddPermissionsEventType) => void;
};

const PermissionsNeeded: React.FC<PermissionsNeededProps> = ({
  permissionsNeededProviders,
  customerId,
  permissionsNeededAccounts,
  nonHealthyOrganizationCloudConnects,
  hasMissingCloudConnectPermissions,
  onAddPermissionsClicked,
}) => {
  const history = useHistory();

  const trustedAdvisorAccounts = permissionsNeededAccounts[InsightProviderId.TRUSTED_ADVISOR];
  const costOptimizationHubAccounts = permissionsNeededAccounts[InsightProviderId.COST_OPTIMIZATION_HUB];
  const securityHubAccounts = permissionsNeededAccounts[InsightProviderId.SECURITY_HUB];

  const providers: Providers = {
    [InsightProviderId.TRUSTED_ADVISOR]: {
      cloudTag: "aws",
      title: "AWS Trusted Advisor",
      description: "Enable Trusted Advisor insights on your accounts with an AWS Business or Enterprise Support plans.",
      buttonText: "Go to AWS settings",
      url: `/customers/${customerId}/integrate/aws?filter=${trustedAdvisorAccounts.toString()}`,
      eventType: "addTAPermissionClickedEvent",
    },
    [InsightProviderId.COST_OPTIMIZATION_HUB]: {
      cloudTag: "aws",
      title: "AWS Cost Optimization Hub",
      description:
        "Enable Cost Optimization Hub insights on your accounts that have opted in to Cost Optimization Hub and AWS Compute Optimizer.",
      buttonText: "Go to AWS settings",
      url: `/customers/${customerId}/integrate/aws?filter=${costOptimizationHubAccounts.toString()}`,
      eventType: "addCostOptPermissionClickedEvent",
    },
    [InsightProviderId.SECURITY_HUB]: {
      cloudTag: "aws",
      title: "AWS Security Hub",
      description:
        "Enable Security Hub insights on your accounts to assess your AWS environment against security industry standards and best practices.",
      buttonText: "Go to AWS settings",
      url: `/customers/${customerId}/integrate/aws?filter=${securityHubAccounts.toString()}`,
      eventType: "addSecurityHubPermissionClickedEvent",
    },
    [InsightProviderId.GCP_RECOMMENDER]: {
      cloudTag: "gcp",
      title: "Google Cloud Recommender",
      description: "Set-up and connect your Google Cloud Recommender BigQuery export to get these recommendations.",
      buttonText: "Go to Google Cloud settings",
      url: `/customers/${customerId}/integrate/gcp/connect/dataset`,
      eventType: "addGCPRecommenderPermissionClickedEvent",
    },
    ...(nonHealthyOrganizationCloudConnects.length === 1 && !hasMissingCloudConnectPermissions
      ? {
          [InsightProviderId.FSK8S]: {
            cloudTag: "gcp",
            title: "Google Kubernetes Engine",
            description:
              "Enable Kubernetes core on your organization to get recommendations for your Google Kubernetes Engine (GKE) clusters.",
            buttonText: "Go to Google Cloud settings",
            url: `/customers/${customerId}/integrate/gcp/connect/organization/${nonHealthyOrganizationCloudConnects[0]}`,
            eventType: "addGKEPermissionClickedEvent",
          },
        }
      : {
          [InsightProviderId.FSK8S]: {
            cloudTag: "gcp",
            title: "Google Kubernetes Engine",
            description:
              "Connect your organization or project and enable Kubernetes core for your Google Kubernetes Engine (GKE) clusters.",
            buttonText: "Go to Google Cloud settings",
            url: `/customers/${customerId}/integrate/gcp`,
            eventType: "addGKEPermissionClickedEvent",
          },
        }),
  };

  const handleAddPermissionsClick = (id: InsightProviderId) => {
    onAddPermissionsClicked(providers[id].eventType);
    history.push(providers[id].url);
  };

  return (
    <>
      <Typography fontWeight={500} gutterBottom>
        Additional permissions needed
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(274px, 1fr)",
            sm: "repeat(auto-fit, minmax(274px, 1fr))",
            md: "repeat(3, 274px)",
          },
          justifyContent: { xs: "center", md: "space-between" },
          gap: 2,
        }}
      >
        {permissionsNeededProviders.map(({ id, insightsNum }) => (
          <ProviderCard
            key={id}
            cloudTag={providers[id].cloudTag}
            title={providers[id].title}
            insightsNum={insightsNum}
            description={providers[id].description}
            buttonText={providers[id].buttonText}
            onButtonClick={() => {
              handleAddPermissionsClick(id);
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default PermissionsNeeded;
